import React, {
  useEffect,
  useCallback,
  useState,
  useRef,
  useMemo,
} from 'react';

import { cacaClient } from '@plangrid-private/web-api-clients';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  useCurrentProjectIsTemplate,
  useCurrentProjectId,
} from '@adsk/alloy-provider';
import { DotSpokeThreeIcon, FolderIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import Theme from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';
import {
  Tree,
  TreeNode,
  useTree,
  ProgressNode,
  TREE_ACTIONS,
} from '@adsk/alloy-react-tree';

import FormTemplatesCustomPickerEmptyState from './FormTemplatesCustomPickerEmptyState';
import {
  FORM_TEMPLATES_FILTERS_DEFAULT_VALUES,
  FORM_TEMPLATE_PLUGIN_METADATA,
  TREE_PAGE_SIZE,
  FOLDER_TYPE,
  EMPTY_ROOT_FOLDER_ENTITY_ID,
  FEATURE_FLAGS,
} from '../helpers/constants';
import { getFormTemplateUrn } from '../helpers/utils';
import { usePlangridFeatureFlag } from '../PlangridLaunchDarklyProvider';

const TreeNodeLabel = styled.div`
  width: 100%;
  overflow: hidden;
`;

const LoadMoreContainer = styled.div`
  ${{ ...Theme.typography.bodySmall }};
  display: flex;
  align-items: center;
  padding-left: 24px;
  height: 50px;
`;

const LoadMoreText = styled.span`
  ${{ ...Theme.typography.bodySmall }};
  margin-left: 13px;
  color: ${Theme.colors.charcoal700};
`;

const STYLES = {
  ScrollContainer: {
    overflowY: 'auto',
  },
  Tree: {
    margin: 0,
  },
  TreeNode: {
    borderBottom: `1px solid ${Theme.colors.charcoal100}`,
    height: '48px',
  },
  CheckMarkIcon: { marginRight: 8, marginBottom: -5 },
  DotSpokeThreeIcon: { marginLeft: 8, marginBottom: -5 },
};

const FormTemplatesTreeView = ({
  entities,
  disabledIds,
  selectedIds,
  filters,
  multiple,
  loading,
  setSelection,
  addEntitiesToCache,
  dataViewProps,
  error,
  onChange,
  isLoadingFolders,
  folders,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const projectIsTemplate = useCurrentProjectIsTemplate();
  const projectId = useCurrentProjectId();
  const [isLoadingMoreTemplates, setIsLoadingMoreTemplates] = useState(false);
  const [loadedAllRootTemplates, setLoadedAllRootTemplates] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [_, setTreeItemsMap] = useState({});
  const treeRef = useRef();
  const { value: isLibraryComponentsEnabled } = usePlangridFeatureFlag(
    FEATURE_FLAGS.ENABLE_LIBRARY_COMPONENTS,
    false,
  );

  const [disabledIdsWithInherited, selectedIdsWithInherited] = useMemo(() => {
    const inheritedTemplateIds =
      dataViewProps && dataViewProps.inheritedTemplateIds
        ? dataViewProps.inheritedTemplateIds.map(getFormTemplateUrn)
        : [];

    const disabledLibraryTemplateIds =
      projectIsTemplate && !isLibraryComponentsEnabled
        ? entities
            .filter((entity) => Boolean(entity.is_component))
            .map((entity) => entity.id)
        : [];

    return [
      [...disabledIds, ...disabledLibraryTemplateIds, ...inheritedTemplateIds],
      [...selectedIds, ...inheritedTemplateIds],
    ];
  }, [
    disabledIds,
    selectedIds,
    dataViewProps,
    projectIsTemplate,
    isLibraryComponentsEnabled,
    entities,
  ]);

  const selectedObj = selectedIdsWithInherited?.reduce((acc, cur) => {
    acc[cur] = true;
    return acc;
  }, {});

  const {
    orderedIds,
    normalizedTree,
    getTreeNodeProps,
    getTreeProps,
    dispatch,
  } = useTree({
    denormalizedTree: treeData,
    getLabelFromNode: ({ name }) => name,
    getIdFromNode: ({ id }) => id,
    selectedIds: selectedObj,
  });

  const fetchMoreRootTemplates = useCallback(async () => {
    const skip = treeData.filter(({ folder_uid }) => !folder_uid).length;

    const { items, total } = await cacaClient.fieldReports.getFieldReportTypes(
      projectId,
      {
        ...FORM_TEMPLATES_FILTERS_DEFAULT_VALUES,
        limit: TREE_PAGE_SIZE,
        folder_uids: ['null'],
        skip,
      },
    );

    if (skip >= total) {
      setLoadedAllRootTemplates(true);
    }

    // Update treeItemsMap and treeData
    const newEntities = items.map((template) => ({
      ...template,
      id: getFormTemplateUrn(template.uid),
    }));
    setTreeItemsMap((treeItemsMap) => {
      const newTreeItemsMap = { ...treeItemsMap };
      const newNodes = newEntities.map((entity) => {
        const newNode = {
          ...entity,
          fetched: true,
          children: undefined,
        };
        newTreeItemsMap[entity.uid] = newNode;
        return newNode;
      });

      // Update state
      setTreeData((treeData) => {
        return [...treeData, ...newNodes];
      });

      return newTreeItemsMap;
    });

    addEntitiesToCache({
      entities: newEntities,
      domain: FORM_TEMPLATE_PLUGIN_METADATA.DOMAIN,
      type: FORM_TEMPLATE_PLUGIN_METADATA.TYPE,
    });

    setIsLoadingMoreTemplates(false);
  }, [addEntitiesToCache, projectId, treeData]);

  const fetchNode = useCallback(
    async (node) => {
      const { items } = await cacaClient.fieldReports.getFieldReportTypes(
        projectId,
        {
          ...FORM_TEMPLATES_FILTERS_DEFAULT_VALUES,
          folder_uids: [node.id],
        },
      );

      // Update treeItemsMap and treeData
      const newEntities = items.map((template) => ({
        ...template,
        id: getFormTemplateUrn(template.uid),
      }));

      let newTreeItemsMap = {};
      const newNodes = newEntities.map((entity) => {
        const newNode = {
          ...entity,
          fetched: true,
          children: undefined,
        };
        newTreeItemsMap[entity.uid] = newNode;
        return newNode;
      });

      setTreeItemsMap((treeItemsMap) => {
        newTreeItemsMap = { ...newTreeItemsMap, ...treeItemsMap };

        // Update parents of folder
        const updateParents = (itemsMap, nodeId, updatedChildren) => {
          itemsMap[nodeId] = {
            ...itemsMap[nodeId],
            fetched: true,
            children: [...itemsMap[nodeId].children],
          };

          updatedChildren.forEach((child) => {
            const index = itemsMap[nodeId].children.findIndex(
              (c) => c.uid === child.uid,
            );
            if (index > -1) {
              itemsMap[nodeId].children[index] = child;
            } else {
              itemsMap[nodeId].children.push(child);
            }
          });

          if (itemsMap[nodeId].parent_uid) {
            updateParents(itemsMap, itemsMap[nodeId].parent_uid, [
              itemsMap[nodeId],
            ]);
          }
        };

        updateParents(newTreeItemsMap, node.uid, newNodes);

        // Insert all updated root elements
        setTreeData((treeData) => {
          return treeData.map(({ uid }) => newTreeItemsMap[uid]);
        });

        // Update state
        return newTreeItemsMap;
      });

      addEntitiesToCache({
        entities: newEntities,
        domain: FORM_TEMPLATE_PLUGIN_METADATA.DOMAIN,
        type: FORM_TEMPLATE_PLUGIN_METADATA.TYPE,
      });

      return newNodes;
    },

    [projectId, addEntitiesToCache],
  );

  useEffect(() => {
    // Build treeItemsMap and treeData
    if (!isLoadingFolders && !loading && !filters.searchExpression) {
      const allTreeItems = [
        ...folders,
        ...entities.filter(({ id }) => id !== EMPTY_ROOT_FOLDER_ENTITY_ID),
      ];
      const newTreeItemsMap = {};

      allTreeItems.forEach(
        (item) =>
          (newTreeItemsMap[item.uid] = {
            ...item,
            id: item.type === FOLDER_TYPE ? item.uid : item.id,
            icon: item.type,
            fetched: item.type !== FOLDER_TYPE,
            children: item.type === FOLDER_TYPE ? [] : undefined,
          }),
      );

      // Build treeData
      const newTreeData = [];
      Object.values(newTreeItemsMap).forEach((node) => {
        if (node.folder_uid || node.parent_uid) {
          newTreeItemsMap[node.folder_uid || node.parent_uid].children.push(
            node,
          );
        } else {
          newTreeData.push(node);
        }
      });

      // Update state
      setTreeItemsMap(newTreeItemsMap);
      setTreeData(newTreeData);
    }
  }, [isLoadingFolders, loading, filters, entities, folders]);

  useEffect(() => {
    dispatch({
      type: TREE_ACTIONS.regenerateTree,
      payload: {
        denormalizedTree: treeData,
      },
    });
  }, [treeData, dispatch]);

  const handleTreeNodeExpand = async ({ id, isExpanded }) => {
    const { original } = normalizedTree[id];
    dispatch({
      type: isExpanded ? TREE_ACTIONS.expandNode : TREE_ACTIONS.collapseNode,
      payload: {
        id,
      },
    });
    if (!original?.fetched) {
      await fetchNode(original);
    }
  };

  const handleTreeNodeSelect = ({ id, isSelected }) => {
    const { original } = normalizedTree[id];
    if (multiple) {
      onChange({
        entity: original,
        checked: isSelected,
      });
    } else {
      const currentNode = isSelected ? [original] : [];
      setSelection(currentNode);
    }
  };

  const handleTreeScroll = useCallback(() => {
    if (
      treeRef?.current &&
      !error &&
      !loading &&
      !loadedAllRootTemplates &&
      !isLoadingMoreTemplates
    ) {
      const { scrollTop, scrollHeight, offsetHeight } = treeRef?.current;
      if (Math.round(scrollTop) === Math.round(scrollHeight - offsetHeight)) {
        setIsLoadingMoreTemplates(true);
        fetchMoreRootTemplates();
      }
    }
  }, [
    error,
    fetchMoreRootTemplates,
    loading,
    loadedAllRootTemplates,
    isLoadingMoreTemplates,
  ]);

  return treeData && treeData.length > 0 ? (
    <div
      style={STYLES.ScrollContainer}
      ref={treeRef}
      onScroll={handleTreeScroll}
    >
      <Tree {...getTreeProps()} style={STYLES.Tree}>
        {orderedIds.map((id) => {
          const normalizedTreeData = normalizedTree[id];
          const treeNodeProps = getTreeNodeProps(normalizedTreeData);
          const { isSelected, isExpanded, depth, label } = treeNodeProps;
          const { original } = normalizedTreeData;
          const { fetched, children, type, is_component } = original;
          const isTemplateUnsupported =
            is_component && projectIsTemplate && !isLibraryComponentsEnabled;

          const renderLabel = (
            <TreeNodeLabel>
              {!children ? (
                <Tooltip
                  content={
                    isTemplateUnsupported
                      ? T.translate('LIBRARY_FORMS_NOT_SUPPORTED')
                      : null
                  }
                >
                  {label}
                  {is_component && (
                    <DotSpokeThreeIcon
                      size={20}
                      style={STYLES.DotSpokeThreeIcon}
                    />
                  )}
                </Tooltip>
              ) : (
                label
              )}
            </TreeNodeLabel>
          );

          const isExpandable = !fetched || children?.length > 0;

          return (
            <>
              <TreeNode
                {...treeNodeProps}
                style={STYLES.TreeNode}
                key={id}
                depth={!isExpandable ? depth - 1 : depth}
                isExpandable={isExpandable}
                isMultiSelectable={type !== FOLDER_TYPE && multiple}
                isSingleSelectable={type !== FOLDER_TYPE && !multiple}
                isSelected={isSelected}
                isExpanded={!!isExpanded}
                isDisabled={
                  isTemplateUnsupported || disabledIdsWithInherited.includes(id)
                }
                onExpand={handleTreeNodeExpand}
                onSelect={handleTreeNodeSelect}
                renderLeftElement={
                  type === FOLDER_TYPE ? FolderIcon.renderIcon : null
                }
                label={renderLabel}
              />
              <ProgressNode show={!!isExpanded && !fetched} depth={depth + 1} />
            </>
          );
        })}
      </Tree>
      {!loadedAllRootTemplates && isLoadingMoreTemplates && (
        <LoadMoreContainer>
          <ProgressRing size={ProgressRing.SIZES.SMALL} />
          <LoadMoreText>{T.translate('LOADING_MORE')}</LoadMoreText>
        </LoadMoreContainer>
      )}
    </div>
  ) : (
    <FormTemplatesCustomPickerEmptyState />
  );
};

FormTemplatesTreeView.displayName = 'FormTemplatesTreeView';

FormTemplatesTreeView.propTypes = {
  /**
   * Whether is loading Forms from BE.
   */
  loading: PropTypes.bool,
  /**
   * IDs of selected Form entities.
   */
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * IDs of Form entities we want to mark as disabled.
   */
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether the component is multi selectable.
   */
  multiple: PropTypes.bool,
  /**
   * List of entities.
   */
  entities: PropTypes.arrayOf(PropTypes.object),
  /**
   * Callback to set Selections.
   * Handler is of shape: (selectedNodes) => *
   */
  setSelection: PropTypes.func,
  /**
   * The currently applied filters.
   */
  filters: PropTypes.object,
  /**
   * callback to add entities to cache. Shape: ({ entities, domain, type }) => *.
   */
  addEntitiesToCache: PropTypes.func,
  /**
   * Additional props to pass to DataView.
   */
  dataViewProps: PropTypes.shape({
    inheritedTemplateIds: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default FormTemplatesTreeView;
